<template>
  <section>
    <div class="row gx-0">
      <!-- image -->
      <transition name="image-transition" mode="out-in">
        <div class="col-lg-6 px-lg-0 d-none d-lg-block" :key="imageSource">
          <div class="interested-img" :style="headerImageStyle"></div>
        </div>
      </transition>
      <div class="col-lg-6 col-12">
        <div
          class="bg-white interested-div pt-space-lg pb-space-md px-space-sm p-lg-space-xl">
          <div class="pl-0">
            <div class="pb-space-lg pb-md-space-sm pb-lg-space-md">
              <component
                class="mb-space-sm"
                :is="titleLevel"
                :class="titleClass">
                {{ title }}
              </component>
              <slot name="body"> </slot>

              <span class="fw-bold">
                Explore information about

                <rds-dropdown
                  :id="`sea-dropdown-${Date.now()}`"
                  class="explore-btn py-space-xxs d-inline-block"
                  menu-class="bg-light-2 border-0 my-space-xxs"
                  buttonSize="md"
                  :text="items[selectedItemIndex].title"
                  :dropup="dropup"
                  :dropdown="dropdown"
                  button-class="d-flex align-items-center"
                  @toggle="
                    selectItemClick(items[selectedItemIndex].title, $event)
                  ">
                  <rds-dropdown-item
                    v-for="(item, index) in informationItems"
                    :key="index"
                    @click="updateSelectedItem(item, index, $event)"
                    link-class="fw-bold">
                    {{ item }}</rds-dropdown-item
                  >
                </rds-dropdown>
              </span>
            </div>
          </div>

          <transition name="links-block" mode="out-in">
            <div :key="selectedItemIndex">
              <!-- list of items -->
              <div class="row">
                <div class="col-12 col-md-6">
                  <ul class="list-unstyled mb-0 pl-0">
                    <li
                      v-for="(listItem, index) in listItems"
                      :key="index"
                      class="pb-space-sm pb-lg-space-md link-pulse">
                      <!-- link -->
                      <a
                        :href="listItem.uri"
                        :title="listItem.text"
                        :aria-label="
                          items[selectedItemIndex].title + '-' + listItem.text
                        "
                        :target="listItem.target"
                        class="fs-medium text-dark-3 fw-bold text-decoration-none"
                        @click="
                          handleLinkClick(
                            {
                              ...listItem,
                              component: items[selectedItemIndex].title,
                            },
                            $event
                          )
                        ">
                        <!-- icon -->
                        <img
                          alt="right-chevron"
                          :src="rightArrow"
                          class="pe-space-xxs" />

                        {{ listItem.text }}
                      </a>
                    </li>
                  </ul>
                </div>
                <div class="col-12 col-md-6">
                  <ul class="list-unstyled pl-0">
                    <li
                      v-for="(listItem, index) in additionalListItems"
                      :key="index"
                      class="pb-space-sm pb-lg-space-md link-pulse">
                      <!-- link -->
                      <a
                        :title="listItem.text"
                        :aria-label="
                          items[selectedItemIndex].title + '-' + listItem.text
                        "
                        :href="listItem.uri"
                        :target="listItem.target"
                        class="fs-medium text-dark-3 fw-bold text-decoration-none"
                        @click="
                          handleLinkClick(
                            {
                              ...listItem,
                              component: items[selectedItemIndex].title,
                            },
                            $event
                          )
                        ">
                        <!-- icon -->
                        <img
                          alt="right-chevron"
                          :src="rightArrow"
                          class="pe-space-xxs" />
                        {{ listItem.text }}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { RdsDropdown, RdsDropdownItem } from "@rds-vue-ui/rds-dropdown";
import { computed, onMounted, ref } from "vue";
import rightArrow from "./assets/arrow-right-solid.svg?url";

export interface ExploreItemLink {
  text: string;
  uri: string;
  target: string;
}

export interface ExploreItemLinkEvent extends ExploreItemLink {
  component: string;
}

export interface ExploreItem {
  title: string;
  image: string;
  listItems: ExploreItemLink[];
}

interface Props {
  items: ExploreItem[];
  title?: string;
  titleLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  titleSize?: "small" | "medium" | "large" | "xl";
  titleVariant?: string;
}

const props = withDefaults(defineProps<Props>(), {
  titleLevel: "h2",
  titleSize: "medium",
  titleVariant: "dark-3",
});

// events
const emits = defineEmits<{
  /**
   *  Emitted when a link is clicked.
   */
  (e: "linkClick", event: Event, item: ExploreItemLinkEvent): void;
  /**
   *  This event is fired when we click on dropdown selection.
   */
  (e: "selectItem", event: Event, item: string): void;
  /**
   *  This event is fired when we clik on dropdownlist.
   */
  (e: "updateItem", event: Event, item: string): void;
}>();

const dropup = ref(false);
const dropdown = ref(true);
const selectedItemIndex = ref(0);
const informationItems = ref<string[]>([]);
const imageSource = ref("");

const titleClass = computed(() => {
  return `${props.titleLevel}-${props.titleSize} text-${props.titleVariant}`;
});

const listItems = computed(() => {
  let allItemTypes = props.items[selectedItemIndex.value].listItems;

  let end = allItemTypes.length === 5 ? 3 : 4;
  return allItemTypes.slice(0, end);
});

const additionalListItems = computed(() => {
  let allItemTypes = props.items[selectedItemIndex.value].listItems;

  let end = allItemTypes.length === 5 ? 3 : 4;
  return allItemTypes.slice(end);
});

const headerImageStyle = computed(() => {
  return {
    "background-image": `linear-gradient(to right,transparent 0,var(--rds-white, #ffffff) 100%),url('${imageSource.value}')`,
  };
});

const handleLinkClick = (item: ExploreItemLinkEvent, event: Event) => {
  emits("linkClick", event, item);
};

const selectItemClick = (selectedItem: string, event: Event) => {
  emits("selectItem", event, selectedItem);
};

const updateSelectedItem = (item: string, index: number, event: Event) => {
  selectedItemIndex.value = index;
  updateImage(index);
  emits("updateItem", event, item);
};

const updateImage = (index: number) => {
  imageSource.value = props.items[index].image;
};

onMounted(() => {
  informationItems.value = props.items.map((obj) => obj.title);
  updateImage(0);
});
</script>

<style lang="scss" scoped>
.links-block-enter-from {
  transform: translate(-25%, 0);
  opacity: 0;
}

.links-block-enter-to {
  opacity: 1;
}

.links-block-enter-active {
  transition: all 0.6s ease 0.3s;
}

.image-transition-enter-from {
  opacity: 0;
  transform: translate(-100%, 0);
}

.image-transition-enter-to {
  opacity: 1;
}

.image-transition-enter-active {
  transition: all 0.9s ease;
}

.link-pulse {
  transition: 0.3s;
  cursor: pointer;
}

.link-pulse:hover {
  transform: scale(1.04) translateX(8px);
}

.dropdown {
  display: inline-block;
  font-size: 16px;
  cursor: pointer;
}

.interested-img {
  background-position: 100.5% top;
  background-repeat: no-repeat;
  background-size: cover;
  height: 900px;
}
.interested-div {
  min-height: 450px;
}

@media (min-width: 992px) {
  .interested-div {
    margin-left: -125px;
    margin-top: 128px;
    height: 650px;
    max-width: 729px;
    transform: translate(0, 0);
  }
}
</style>
